import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import { useState, useCallback, useMemo } from "react";
import { View, Text, ScrollView, Platform } from "react-native";
import {
  Ban,
  CircleHelp,
  Layers,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { getBlocksByKind, type ContentBlock } from "@src/packages/content";
import { EMBER } from "../../../onboarding/theme";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import { MoonFilterChip, MoonSearchInput, BackLink } from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import TherapyCard from "../../components/access/TherapyCard";
import {
  useLocale,
  useTranslations,
  type TranslationFunction,
} from "@src/packages/i18n";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";
import { colors } from "@louise/theme/tokens";

/* ─── Category display config ────────────────────────────────────── */

type EvidenceFilter =
  "all" | "strong" | "moderate" | "emerging" | "mixed" | "debunked";

function buildEvidenceFilters(t: TranslationFunction): {
  label: string;
  value: EvidenceFilter;
  icon: LucideIcon;
}[] {
  return [
    {
      label: t("access.therapies.evidence_filter.all"),
      value: "all",
      icon: Layers,
    },
    {
      label: t("access.therapies.evidence_filter.strong"),
      value: "strong",
      icon: ShieldCheck,
    },
    {
      label: t("access.therapies.evidence_filter.moderate"),
      value: "moderate",
      icon: TrendingUp,
    },
    {
      label: t("access.therapies.evidence_filter.emerging"),
      value: "emerging",
      icon: Sparkles,
    },
    {
      label: t("access.therapies.evidence_filter.mixed"),
      value: "mixed",
      icon: CircleHelp,
    },
    {
      label: t("access.therapies.evidence_filter.debunked"),
      value: "debunked",
      icon: Ban,
    },
  ];
}

const EVIDENCE_SORT_RANK: Record<string, number> = {
  strong: 0,
  moderate: 1,
  emerging: 2,
  mixed: 3,
  debunked: 4,
};

type TherapyCategory = ContentBlock<"therapy">["body"]["category"];

const THERAPY_CATEGORY_SORT_RANK: Record<TherapyCategory, number> = {
  clinical: 0,
  physical: 1,
  traditional: 2,
  "mind-body": 3,
  nutritional: 4,
  lifestyle: 5,
};

type Props = NativeStackScreenProps<AccessStackParamList, "Therapies">;

export default function TherapiesScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "AccessOverview",
  });
  const { locale } = useLocale();
  const [search, setSearch] = useState("");
  const [evidenceFilter, setEvidenceFilter] = useState<EvidenceFilter>("all");

  const therapies = useMemo(() => getBlocksByKind("therapy"), []);
  const evidenceFilters = useMemo(() => buildEvidenceFilters(t), [t]);

  const filtered = useMemo(() => {
    const bodyFor = (item: ContentBlock<"therapy">) =>
      locale === "fr" && item.bodyFr ? item.bodyFr : item.body;
    let result = [...therapies];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((item) => {
        const body = bodyFor(item);
        return (
          body.name.toLowerCase().includes(q) ||
          item.title.toLowerCase().includes(q) ||
          body.description.toLowerCase().includes(q)
        );
      });
    }

    if (evidenceFilter !== "all") {
      result = result.filter(
        (item) => bodyFor(item).evidenceStrength === evidenceFilter,
      );
    }

    result.sort((a, b) => {
      const aBody = bodyFor(a);
      const bBody = bodyFor(b);
      const aCategoryRank = THERAPY_CATEGORY_SORT_RANK[aBody.category] ?? 99;
      const bCategoryRank = THERAPY_CATEGORY_SORT_RANK[bBody.category] ?? 99;
      if (aCategoryRank !== bCategoryRank) {
        return aCategoryRank - bCategoryRank;
      }

      const aRank = EVIDENCE_SORT_RANK[aBody.evidenceStrength] ?? 99;
      const bRank = EVIDENCE_SORT_RANK[bBody.evidenceStrength] ?? 99;
      if (aRank !== bRank) return aRank - bRank;
      return aBody.name.localeCompare(bBody.name);
    });

    return result;
  }, [therapies, search, evidenceFilter, locale]);

  const onItemPress = useCallback(
    (item: ContentBlock<"therapy">) => {
      navigation.navigate("TherapyDetail", { therapyId: item.id });
    },
    [navigation],
  );

  // Pair items into rows of 2
  const rows = useMemo(() => {
    const result: ContentBlock<"therapy">[][] = [];
    for (let i = 0; i < filtered.length; i += 2) {
      result.push(filtered.slice(i, i + 2));
    }
    return result;
  }, [filtered]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <BackLink
          label={
            route.params?.returnTab ? t("you.shared.back") : t("access.back")
          }
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={
            {
              fontSize: 38,
              fontWeight: "700",
              color: colors.deepBrown,
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            } as Record<string, unknown>
          }
        >
          {t("access.therapies.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 21,
          }}
        >
          {t("access.therapies.subtitle")}
        </Text>

        <MoonSearchInput
          placeholder={t("access.therapies.search_placeholder")}
          value={search}
          onChangeText={setSearch}
          onClear={() => setSearch("")}
          tone="glass"
          style={{ marginTop: 18 }}
        />

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 8,
            paddingRight: 4,
          }}
          style={{
            marginTop: 12,
            marginHorizontal: -2,
            paddingHorizontal: 2,
          }}
        >
          {evidenceFilters.map((filter) => (
            <MoonFilterChip
              key={filter.value}
              label={filter.label}
              icon={filter.icon}
              selected={evidenceFilter === filter.value}
              onPress={() => setEvidenceFilter(filter.value)}
              tone="glass"
              showCheck={false}
            />
          ))}
        </ScrollView>

        {/* 2x2 Grid */}
        <View style={{ gap: 12, marginTop: 16 }}>
          {rows.map((row, rowIdx) => (
            <View key={rowIdx} style={{ flexDirection: "row", gap: 12 }}>
              {row.map((therapy) => (
                <TherapyCard
                  key={therapy.id}
                  therapy={therapy}
                  onPress={onItemPress}
                />
              ))}
              {row.length === 1 && <View style={{ flex: 1 }} />}
            </View>
          ))}
        </View>

        {/* Empty state */}
        {filtered.length === 0 && (
          <SearchEmptyState
            title={t("access.therapies.empty")}
            onReset={() => {
              setSearch("");
              setEvidenceFilter("all");
            }}
          />
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
